import React, { useState, useEffect, useRef, useMemo } from 'react';
import { SocialEmbed, PLATFORMS } from './SocialEmbeds';
import { monthlyPoolLabel, monthlyPoolShort } from './leaderboardPrizes';

// Monthly pool as of page load — $40K until the Oct 24 2026 period, $50K after
const POOL = monthlyPoolLabel();

// ============================================
// SNUTZY Landing Page — rendered at "/"
// Self-contained: own styles, scroll animations, no external libs.
// All class names prefixed "lp-" to avoid collisions with App.jsx globals.
// Palette: purple (#a855f7) + white primary; amber/gold (#fbbf24) reserved
// for CTAs, prize badges and highlights — matches the sidebar brand.
// ============================================

const SHUFFLE_URL = 'https://shuffle.com/?r=snutzy';
const TWITCH_URL = 'https://www.twitch.tv/snutzy';
const DISCORD_URL = 'https://discord.gg/snutzy';
const X_URL = 'https://x.com/snutztv';
const INSTAGRAM_URL = 'https://www.instagram.com/snutztv/';
const YOUTUBE_URL = 'https://www.youtube.com/@snutztv';

// ---- Scroll-reveal wrapper (IntersectionObserver) ----
// Drives the animation with direct DOM writes instead of React state: the
// hidden state is committed and a forced reflow guarantees the browser has
// rendered it BEFORE the transition starts. Going through setState lets the
// browser batch both styles into one paint, which skips the transition and
// makes elements "pop" in — the bug this avoids.
const Reveal = ({ children, delay = 0, y = 36, style = {}, className = '' }) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (!('IntersectionObserver' in window)) {
      el.style.opacity = '1';
      el.style.transform = 'none';
      el.style.filter = 'none';
      return;
    }

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        obs.disconnect();
        void el.getBoundingClientRect(); // force layout so the hidden state is committed
        requestAnimationFrame(() => {
          el.style.transition = `opacity 0.9s ease-out ${delay}ms, transform 0.9s ease-out ${delay}ms, filter 0.9s ease-out ${delay}ms`;
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
          el.style.filter = 'blur(0px)';
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        ...style,
        opacity: 0,
        transform: `translateY(${y}px)`,
        filter: 'blur(6px)',
        willChange: 'opacity, transform, filter'
      }}
    >
      {children}
    </div>
  );
};

// ---- Animated number that counts up smoothly when scrolled into view ----
const CountUp = ({ value, prefix = '', suffix = '', decimals = 0, duration = 2000 }) => {
  const ref = useRef(null);
  const [display, setDisplay] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (started.current) { setDisplay(value); return; } // value updated after animation (e.g. API data arrived)

    const run = () => {
      if (started.current) return;
      started.current = true;
      const t0 = performance.now();
      const tick = (now) => {
        const p = Math.min((now - t0) / duration, 1);
        const eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p); // easeOutExpo
        setDisplay(value * eased);
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    if (!('IntersectionObserver' in window)) { run(); return; }
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { run(); obs.disconnect(); } },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [value, duration]);

  const formatted = decimals > 0
    ? display.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    : Math.round(display).toLocaleString();

  return <span ref={ref}>{prefix}{formatted}{suffix}</span>;
};

// ---- Section heading: bracket label + big display title ----
const SectionHead = ({ label, title, sub }) => (
  <div className="lp-section-head">
    <Reveal><div className="lp-section-label">[ {label} ]</div></Reveal>
    <Reveal delay={120}><h2 className="lp-section-title">{title}</h2></Reveal>
    {sub && <Reveal delay={240}><p className="lp-section-sub">{sub}</p></Reveal>}
  </div>
);

// ---- FAQ accordion item ----
const FaqItem = ({ q, a, open, onToggle }) => (
  <div className={`lp-faq-item ${open ? 'open' : ''}`}>
    <button className="lp-faq-question" onClick={onToggle}>
      <span>{q}</span>
      <span className="lp-faq-icon">+</span>
    </button>
    <div className={`lp-faq-body ${open ? 'open' : ''}`}>
      <div><p className="lp-faq-answer">{a}</p></div>
    </div>
  </div>
);

// Live features — each card navigates to its tab
const FEATURES = [
  { icon: '🏆', title: 'Monthly Leaderboard', badge: monthlyPoolShort(), badgeType: 'gold', page: 'leaderboard', desc: `A ${POOL} prize pool every single month. Wager under guild SNUTZY on Shuffle and climb — the top 15 all get paid.` },
  { icon: '💰', title: 'Wager Rewards', badge: '$15K+', badgeType: 'gold', page: 'rewards', desc: 'Seven wager milestones that each pay their own cash reward, from $50 at $25K to $10,000 at $10M, plus $500 for every $250K past $1M. That\'s $15,000+ up for grabs every month.' },
  { icon: '💎', title: 'Points Shop', badge: null, page: 'pointsshop', desc: 'Earn points just by watching and wagering. Redeem for cash, bonus buys and exclusive rewards.' },
  { icon: '🎁', title: 'Chat Giveaways', badge: null, page: 'claim', desc: 'Live keyword giveaways during streams, with bad luck protection that boosts the odds for loyal viewers and players.' },
  { icon: '🎯', title: 'Bonus Hunt Predictions', badge: null, page: 'predictions', desc: 'Guess the ending balance during bonus hunts. Closest predictions win prizes — every hunt is a free chance to cash in.' },
  { icon: '👑', title: 'VIP Rewards', badge: null, page: 'vipdashboard', desc: 'Wager $50K+ (weighted) in the guild to unlock VIP status — exclusive perks, boosted giveaway odds and VIP-only rewards.' }
];

const COMING_SOON = [
  { key: 'lossback', icon: '💸', title: '10% Lossback', desc: 'Loyal guild SNUTZY players get 10% back on their losses.' },
  { key: 'referrals', icon: '🤝', title: 'Recruit & Earn', desc: 'Get paid for referring your friends to the guild.' }
  // Clip & Earn is parked — it stays out of "coming soon" so the flag being off
  // hides it completely. Its LIVE_CARDS entry below still restores the full card
  // the moment feature_clips_enabled is switched back on.
];

// When a feature flag turns on, its coming-soon card graduates to a live, clickable card
const LIVE_CARDS = {
  referrals: { icon: '🤝', title: 'Recruit & Earn', badge: '$685', badgeType: 'gold', page: 'referrals', desc: 'Get paid for every friend you bring to the guild: five milestones per recruit, each paid separately, worth $685 in total as they wager.' },
  clips: { icon: '✂️', title: 'Clip & Earn', badge: '$10/1K', badgeType: 'gold', page: 'clipearn', desc: 'Post SNUTZY clips on YouTube, X, TikTok or Instagram and earn $10 per 1,000 verified views — uncapped.' },
  lossback: { icon: '💸', title: '10% Lossback', badge: '10%', badgeType: 'gold', page: 'lossback', desc: 'Wagered $10K+ (weighted) this period? Claim 10% of your losses back, up to $2,000 every month.' }
};

// Hero rewards grid — the headline offers, visible before any scrolling. Items
// whose feature flag is off drop out; the grid always shows full rows (8 → 4×2,
// 6 → 3×2), so an odd count loses its last, lowest-priority item. Money values
// render gold; word values ("Every stream") get a smaller size.
const HERO_REWARDS = [
  { value: POOL, money: true, label: 'Monthly leaderboard', fact: 'Top 15 get paid', page: 'leaderboard' },
  { value: '$15,000+', money: true, label: 'Wager rewards', fact: 'Up for grabs every month', page: 'rewards' },
  { value: '10%', label: 'Lossback', fact: 'Up to $2,000 a month', page: 'lossback', flag: 'lossback' },
  { value: '$685', money: true, label: 'Per recruit', fact: 'Across 5 milestones', page: 'referrals', flag: 'referrals' },
  { value: 'Every stream', text: true, label: 'VIP Discord giveaways', fact: 'With bad luck protection', page: 'vipdashboard' },
  { value: 'VIP Fridays', text: true, label: 'Free hunt equity', fact: 'In our Discord raffle hunts', page: 'communityhunt', sub: 'raffle' },
  { value: 'Points', text: true, label: 'Points Shop', fact: 'Cash, bonus buys and more', page: 'pointsshop' },
  { value: 'Live', text: true, label: 'Chat giveaways', fact: 'With bad luck protection', page: 'claim' }
];

const STEPS = [
  { n: '01', title: 'Sign Up on Shuffle', desc: <>Create your Shuffle account and join guild <strong>SNUTZY</strong>. It takes less than a minute and instantly connects you to the community.</> },
  { n: '02', title: 'Link Your Account', desc: <>Login with Twitch here on snutzy.gg and link your Shuffle username so your wagers and rewards are tracked automatically.</> },
  { n: '03', title: 'Earn & Win', desc: <>Climb the leaderboards, earn wager rewards, join giveaways and raffles. The more you play and watch, the more you win.</> }
];

const FAQS = [
  {
    q: 'How do I sign up?',
    a: 'Create a Shuffle account under guild SNUTZY (or click any "Sign Up with Shuffle" button on this page). Then come back to snutzy.gg, log in with Twitch and link your Shuffle username. That\'s it — you\'re on the leaderboard.'
  },
  {
    q: 'What rewards can I earn?',
    a: `Monthly leaderboard prizes from a ${POOL} pool, $15,000+ in wager rewards every month (seven milestones that each pay their own cash reward, plus $500 for every $250K you wager past $1M), chat giveaways during streams, bonus hunt prediction winnings, and points you can redeem in the Points Shop for cash, bonus buys and exclusive rewards.`
  },
  {
    q: 'How does the leaderboard work?',
    a: `Every dollar you wager on Shuffle under guild SNUTZY counts toward your leaderboard position. The monthly race runs from the 24th to the 24th, and the top 15 players split the ${POOL} prize pool.`
  },
  {
    q: 'What is bad luck protection?',
    a: 'A boost for loyal community members. If you\'ve wagered $5,000+ or watched 40+ hours of streams in the last two months, you qualify for bad luck protection — which improves your odds in chat giveaways. Running cold doesn\'t mean leaving empty-handed.'
  },
  {
    q: 'How do I claim prizes?',
    a: 'Loyal viewers and guild SNUTZY members usually get payouts processed automatically. If you\'re newer, head to the Claim Giveaway page to submit your claim. You\'ll be able to see all pending giveaways there — just make sure your Twitch account, Discord username, and Shuffle username are all linked and correct on your profile.'
  }
];

export default function LandingPage({ navigateTo, leaderboard = [], isStreamerLive = false, publicPayoutStats = null, prizes = [], activePlayers = null, featuredPosts = [], featureFlags = {} }) {
  const [openFaq, setOpenFaq] = useState(0);

  // Gentle scroll assist: proximity snap nudges the viewport to the nearest
  // section edge when the user stops scrolling close to one. Scoped to the
  // landing page only (class on <html> removed on unmount).
  useEffect(() => {
    document.documentElement.classList.add('lp-snap');
    return () => document.documentElement.classList.remove('lp-snap');
  }, []);

  // Deterministic-per-mount particle field for the hero. One hue family
  // (three purples), slower drift, larger ones blurred for bokeh depth —
  // reads as embers rising through the brand color, not confetti.
  const particles = useMemo(() =>
    Array.from({ length: 18 }, (_, i) => {
      const size = 2 + Math.random() * 4.5;
      return {
        left: Math.random() * 100,
        size,
        duration: 13 + Math.random() * 16,
        delay: Math.random() * 16,
        color: ['#7c3aed', '#a855f7', '#c4b5fd'][i % 3],
        opacity: 0.15 + Math.random() * 0.35,
        blur: size > 4.5 ? 1.5 : 0,
        drift: -30 + Math.random() * 60
      };
    }), []);

  const topFive = leaderboard.slice(0, 5);
  const totalGivenAway = publicPayoutStats?.total > 0 ? Math.round(publicPayoutStats.total) : 250000;
  const giveawayPost = featuredPosts.find(p => p.is_giveaway) || null;
  const liveFeatures = FEATURES.concat(
    Object.keys(LIVE_CARDS).filter(k => featureFlags[k]).map(k => LIVE_CARDS[k])
  );
  const comingSoon = COMING_SOON.filter(c => !featureFlags[c.key]);
  const heroRewardsAll = HERO_REWARDS.filter(r => !r.flag || featureFlags[r.flag]);
  const heroRewards = heroRewardsAll.slice(0, heroRewardsAll.length - (heroRewardsAll.length % 2));
  const heroCols = heroRewards.length >= 8 ? 4 : 3;

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const scrollToStats = () => scrollToId('lp-stats');
  const scrollToRewards = () => scrollToId('lp-rewards');

  const goTo = (page, subPage = null) => {
    navigateTo(page, subPage);
    window.scrollTo(0, 0);
  };

  const cardKeyDown = (e, page) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      goTo(page);
    }
  };

  const prizeFor = (rank) => {
    const p = prizes.find(x => x.rank === rank);
    return p ? `$${p.prize.toLocaleString()}` : null;
  };

  const rankColors = { 1: '#fbbf24', 2: '#cbd5e1', 3: '#d97706' };

  return (
    <div className="lp-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Unbounded:wght@400;500;600;700;800;900&display=swap');

        .lp-root {
          /* transparent so the app-level 3D mesh + glows show through */
          background: transparent;
          color: #fff;
          font-family: 'Outfit', sans-serif;
          overflow-x: hidden;
          position: relative;
        }
        .lp-display { font-family: 'Unbounded', 'Outfit', sans-serif; }

        /* Scroll assist: proximity snap (only engages near section edges) */
        html.lp-snap { scroll-snap-type: y proximity; }
        .lp-snap-point { scroll-snap-align: start; scroll-margin-top: 60px; }

        /* ===== HERO ===== */
        .lp-hero {
          position: relative;
          min-height: calc(100vh - 60px);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 4rem 1.5rem 8.5rem;
          overflow: hidden;
        }
        /* Soft fade into the stats band for a smoother section transition */
        .lp-hero::after {
          content: '';
          position: absolute;
          left: 0; right: 0; bottom: 0;
          height: 130px;
          background: linear-gradient(transparent, rgba(5, 5, 7, 0.85));
          pointer-events: none;
        }
        .lp-aurora {
          position: absolute;
          border-radius: 50%;
          filter: blur(95px);
          pointer-events: none;
        }
        /* The orbs sit in their own layer that fades to nothing at the hero's top
           and bottom edges. That fade lives on the LAYER (fixed to the hero box),
           not on the orbs: per-orb masks drifted with the animation / nav height
           and left the corner orbs ~35% opaque where the hero clips them, which
           read as a hard rectangle on wide monitors. Orbs A and B also use no
           blur — a mask only paints inside its box, so blur spill gets sliced. */
        .lp-aurora-layer {
          position: absolute;
          inset: 0;
          overflow: hidden;
          pointer-events: none;
          -webkit-mask-image: linear-gradient(to bottom, transparent 0, #000 260px, #000 calc(100% - 220px), transparent 100%);
          mask-image: linear-gradient(to bottom, transparent 0, #000 260px, #000 calc(100% - 220px), transparent 100%);
        }
        .lp-aurora-a {
          width: 820px; height: 820px;
          top: -280px; left: 4%;
          filter: none;
          /* long multi-stop tail → no Mach-band ring where the glow terminates */
          background: radial-gradient(circle, rgba(168,85,247,0.19) 0%, rgba(168,85,247,0.10) 24%, rgba(168,85,247,0.045) 40%, rgba(168,85,247,0.018) 52%, rgba(168,85,247,0.006) 62%, transparent 70%);
          animation: lp-drift-a 22s ease-in-out infinite alternate;
        }
        .lp-aurora-b {
          width: 760px; height: 760px;
          bottom: -260px; right: 2%;
          filter: none;
          background: radial-gradient(circle, rgba(251,191,36,0.10) 0%, rgba(251,191,36,0.05) 24%, rgba(251,191,36,0.022) 40%, rgba(251,191,36,0.009) 52%, rgba(251,191,36,0.003) 62%, transparent 70%);
          animation: lp-drift-b 26s ease-in-out infinite alternate;
        }
        .lp-aurora-c {
          width: 420px; height: 420px;
          top: 35%; left: 55%;
          background: radial-gradient(circle, rgba(124,58,237,0.11), transparent 65%);
          animation: lp-drift-a 30s ease-in-out infinite alternate-reverse;
        }
        @keyframes lp-drift-a {
          from { transform: translate(0, 0) scale(1); }
          to   { transform: translate(90px, 60px) scale(1.18); }
        }
        @keyframes lp-drift-b {
          from { transform: translate(0, 0) scale(1.1); }
          to   { transform: translate(-80px, -50px) scale(0.95); }
        }

        .lp-particle {
          position: absolute;
          bottom: -10px;
          border-radius: 50%;
          pointer-events: none;
          animation: lp-float linear infinite;
        }
        @keyframes lp-float {
          0%   { transform: translateY(0) translateX(0); opacity: 0; }
          8%   { opacity: var(--lp-po, 0.5); }
          90%  { opacity: var(--lp-po, 0.5); }
          100% { transform: translateY(-105vh) translateX(var(--lp-px, 28px)); opacity: 0; }
        }

        /* "backwards" fill keeps elements hidden only during the animation delay.
           If animations ever fail to run (old browser, OS override), the base
           state is fully visible — the hero can never end up blank. */
        .lp-hero-enter { animation: lp-rise 0.9s ease-out backwards; }
        @keyframes lp-rise {
          from { opacity: 0; transform: translateY(30px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .lp-live-badge {
          display: inline-flex; align-items: center; gap: 0.5rem;
          padding: 0.45rem 1rem;
          border-radius: 999px;
          font-size: 0.78rem; font-weight: 700;
          letter-spacing: 1.5px; text-transform: uppercase;
          text-decoration: none;
          margin-bottom: 1.75rem;
          transition: transform 0.25s ease, box-shadow 0.25s ease;
        }
        .lp-live-badge:hover { transform: translateY(-2px); }
        .lp-live-badge.live {
          color: #fecaca;
          background: rgba(239,68,68,0.12);
          border: 1px solid rgba(239,68,68,0.4);
          box-shadow: 0 0 24px rgba(239,68,68,0.25);
        }
        .lp-live-badge.offline {
          color: #a1a1aa;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.1);
        }
        .lp-live-dot {
          width: 8px; height: 8px; border-radius: 50%;
        }
        .lp-live-badge.live .lp-live-dot {
          background: #ef4444;
          animation: lp-live-pulse 1.4s ease-in-out infinite;
        }
        .lp-live-badge.offline .lp-live-dot { background: #52525b; }
        @keyframes lp-live-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.6); }
          50%      { box-shadow: 0 0 0 7px rgba(239,68,68,0); }
        }

        .lp-hero-title {
          font-size: clamp(3.2rem, 11vw, 7.5rem);
          font-weight: 900;
          line-height: 0.95;
          letter-spacing: -0.01em;
          margin: 0;
          color: #fff;
          /* hard offset in brand purple — print-misregistration, not glow */
          text-shadow: 0.045em 0.045em 0 rgba(124, 58, 237, 0.55);
        }
        .lp-hero-x {
          display: flex; align-items: center; justify-content: center; gap: 1.1rem;
          margin-top: 0.9rem;
          font-size: clamp(1.15rem, 3.2vw, 2rem);
          font-weight: 700;
          letter-spacing: 0.42em;
          text-indent: 0.42em;
          color: #e9d5ff;
          text-transform: uppercase;
        }
        .lp-hero-x .lp-x-mark {
          color: #a855f7;
          font-size: 1.25em;
          text-indent: 0;
          letter-spacing: 0;
          filter: drop-shadow(0 0 14px rgba(168,85,247,0.7));
        }
        .lp-hero-x .lp-x-line {
          width: clamp(28px, 6vw, 70px); height: 1px;
          background: linear-gradient(90deg, transparent, rgba(168,85,247,0.6));
        }
        .lp-hero-x .lp-x-line.r { background: linear-gradient(90deg, rgba(168,85,247,0.6), transparent); }

        .lp-hero-sub {
          max-width: 580px;
          margin: 1.75rem auto 2.5rem;
          font-size: clamp(1rem, 2vw, 1.18rem);
          line-height: 1.65;
          color: #a1a1aa;
          font-weight: 400;
        }
        .lp-hero-sub strong { color: #c084fc; font-weight: 600; }

        .lp-hero-ctas { display: flex; gap: 1rem; flex-wrap: wrap; justify-content: center; }
        /* Primary CTA — same family as the leaderboard signup button:
           dark plum fill inside a conic purple ring. Gold stays reserved
           for money figures. */
        .lp-btn-primary {
          position: relative;
          display: inline-flex; align-items: center; gap: 0.6rem;
          padding: 1.05rem 2.4rem;
          border-radius: 14px;
          border: 2px solid transparent; cursor: pointer;
          font-family: 'Outfit', sans-serif;
          font-size: 1.02rem; font-weight: 800;
          letter-spacing: 0.4px;
          color: #fff;
          text-decoration: none;
          background:
            linear-gradient(135deg, #2e1b52 0%, #3b2369 55%, #2e1b52 100%) padding-box,
            conic-gradient(from 140deg, #8b5cf6, #b57bff, #d0a5ff, #8f8bff, #7c6cf6, #8b5cf6) border-box;
          box-shadow: 0 8px 30px rgba(0,0,0,0.45), 0 0 30px rgba(139,92,246,0.35), inset 0 1px 0 rgba(255,255,255,0.1);
          overflow: hidden;
          transition: transform 0.25s ease, box-shadow 0.25s ease;
        }
        .lp-btn-primary:hover {
          transform: translateY(-3px) scale(1.02);
          box-shadow: 0 8px 30px rgba(0,0,0,0.45), 0 0 46px rgba(139,92,246,0.5), inset 0 1px 0 rgba(255,255,255,0.1);
        }
        .lp-btn-arrow { transition: transform 0.25s ease; }
        .lp-btn-primary:hover .lp-btn-arrow { transform: translateX(4px); }
        .lp-bob { display: inline-block; animation: lp-bob 1.6s ease-in-out infinite; }
        @keyframes lp-bob {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(4px); }
        }
        .lp-btn-primary::after {
          content: '';
          position: absolute; top: 0; left: -80%;
          width: 36%; height: 100%;
          background: linear-gradient(105deg, transparent, rgba(216,180,254,0.18), transparent);
          transform: skewX(-20deg);
          animation: lp-shine 3.4s ease-in-out infinite;
        }
        @keyframes lp-shine {
          0%       { left: -80%; }
          55%, 100% { left: 130%; }
        }
        /* Oversized final CTA — gentle scale breathe, purple ring glow */
        .lp-btn-mega {
          font-size: clamp(1.05rem, 2.4vw, 1.25rem);
          padding: 1.4rem 3.4rem;
          border-radius: 18px;
          animation: lp-mega-pulse 2.6s ease-in-out infinite;
        }
        @keyframes lp-mega-pulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 26px rgba(139,92,246,0.3), 0 10px 35px rgba(0,0,0,0.5);
          }
          50% {
            transform: scale(1.03);
            box-shadow: 0 0 55px rgba(139,92,246,0.5), 0 10px 35px rgba(0,0,0,0.5);
          }
        }
        .lp-btn-ghost {
          display: inline-flex; align-items: center; gap: 0.5rem;
          padding: 1.05rem 2.2rem;
          border-radius: 14px;
          cursor: pointer;
          font-family: 'Outfit', sans-serif;
          font-size: 1.02rem; font-weight: 700;
          color: #e4e4e7;
          text-decoration: none;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.14);
          backdrop-filter: blur(8px);
          transition: transform 0.25s ease, border-color 0.25s ease, background 0.25s ease;
        }
        .lp-btn-ghost:hover {
          transform: translateY(-3px);
          border-color: rgba(168,85,247,0.55);
          background: rgba(168,85,247,0.1);
        }

        /* Hero rewards grid — same surface language as .lp-feature-card / .lp-stats:
           near-transparent fill, white hairlines, no outer glow. Gold only on money. */
        .lp-hero-rewards { margin: 2.75rem auto 0; max-width: 1100px; container-type: inline-size; }
        .lp-hr-eyebrow {
          font-family: 'Space Mono', monospace;
          font-size: 0.68rem; font-weight: 700;
          letter-spacing: 3px; text-transform: uppercase;
          color: #a78bfa; opacity: 0.85;
          margin-bottom: 1rem;
        }
        .lp-hr-grid {
          display: grid;
          border-radius: 18px;
          background: linear-gradient(160deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01));
          border: 1px solid rgba(255,255,255,0.08);
          overflow: hidden;
        }
        .lp-hr-grid.cols-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
        .lp-hr-grid.cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        .lp-hr-cell {
          position: relative;
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          gap: 0.4rem;
          padding: 1.25rem 1rem 1.15rem;
          background: transparent; border: none; color: inherit;
          font-family: 'Outfit', sans-serif; text-align: center;
          cursor: pointer;
          transition: background 0.3s ease;
        }
        /* hairlines: vertical between columns, horizontal between rows */
        .lp-hr-cell::before {
          content: ''; position: absolute; left: 0; top: 20%; bottom: 20%; width: 1px;
          background: linear-gradient(transparent, rgba(255,255,255,0.1), transparent);
        }
        .lp-hr-grid.cols-4 .lp-hr-cell:nth-child(4n+1)::before,
        .lp-hr-grid.cols-3 .lp-hr-cell:nth-child(3n+1)::before { display: none; }
        .lp-hr-grid.cols-4 .lp-hr-cell:nth-child(n+5),
        .lp-hr-grid.cols-3 .lp-hr-cell:nth-child(n+4) { border-top: 1px solid rgba(255,255,255,0.06); }
        .lp-hr-cell:hover { background: rgba(168,85,247,0.06); }
        .lp-hr-cell:focus-visible { outline: 2px solid rgba(216,180,254,0.8); outline-offset: -3px; }
        .lp-hr-go {
          position: absolute; top: 0.65rem; right: 0.8rem;
          font-size: 0.85rem; color: #c4b5fd;
          opacity: 0; transform: translateX(-4px);
          transition: opacity 0.25s ease, transform 0.25s ease;
        }
        .lp-hr-cell:hover .lp-hr-go, .lp-hr-cell:focus-visible .lp-hr-go { opacity: 1; transform: none; }
        .lp-hr-value {
          font-size: 1.6rem; font-weight: 800; line-height: 1.1; letter-spacing: -0.01em;
          color: #fff; white-space: nowrap;
          text-shadow: 0 0 24px rgba(168,85,247,0.35);
        }
        .lp-hr-value.text { font-size: 1.2rem; padding: 0.2rem 0 0.15rem; }
        .lp-hr-value.money {
          background: linear-gradient(135deg, #fde68a, #fbbf24 45%, #f59e0b);
          -webkit-background-clip: text; background-clip: text;
          color: transparent; text-shadow: none;
        }
        .lp-hr-label {
          font-size: 0.68rem; font-weight: 700;
          letter-spacing: 1.6px; text-transform: uppercase;
          color: #d4d4d8;
        }
        .lp-hr-fact { font-size: 0.74rem; font-weight: 500; color: #8b8b96; line-height: 1.35; }
        /* Narrow columns: two per row */
        @container (max-width: 640px) {
          .lp-hr-grid.cols-4, .lp-hr-grid.cols-3 { grid-template-columns: repeat(2, minmax(0, 1fr)); border-radius: 16px; }
          .lp-hr-grid .lp-hr-cell:nth-child(2n)::before { display: block; }
          .lp-hr-grid .lp-hr-cell:nth-child(2n+1)::before { display: none; }
          .lp-hr-grid .lp-hr-cell:nth-child(n+3) { border-top: 1px solid rgba(255,255,255,0.06); }
          .lp-hr-cell { padding: 1.05rem 0.6rem; gap: 0.3rem; }
          .lp-hr-value { font-size: 1.3rem; }
          .lp-hr-value.text { font-size: 1rem; }
          .lp-hr-go { display: none; }
        }
        @media (prefers-reduced-motion: reduce) {
          .lp-hr-cell, .lp-hr-go { transition: none; }
        }

        .lp-scroll-hint {
          position: absolute;
          bottom: 1.6rem; left: 50%;
          transform: translateX(-50%);
          display: flex; flex-direction: column; align-items: center; gap: 0.55rem;
          color: #c4b5fd;
          font-size: 0.72rem; font-weight: 700;
          letter-spacing: 3px; text-transform: uppercase;
          text-shadow: 0 0 14px rgba(168,85,247,0.55);
          cursor: pointer;
          border: none; background: none;
          font-family: 'Outfit', sans-serif;
          animation: lp-hint-bob 2.4s ease-in-out infinite;
          transition: color 0.25s ease;
        }
        .lp-scroll-hint:hover { color: #fff; }
        @keyframes lp-hint-bob {
          0%, 100% { transform: translate(-50%, 0); }
          50%      { transform: translate(-50%, -6px); }
        }
        .lp-scroll-mouse {
          width: 26px; height: 42px;
          border: 2px solid rgba(168,85,247,0.65);
          border-radius: 14px;
          box-shadow: 0 0 20px rgba(168,85,247,0.35), inset 0 0 8px rgba(168,85,247,0.15);
          display: flex; justify-content: center;
          padding-top: 7px;
        }
        .lp-scroll-mouse span {
          width: 3px; height: 8px;
          border-radius: 3px;
          background: #fbbf24;
          box-shadow: 0 0 8px rgba(251,191,36,0.8);
          animation: lp-scroll-dot 1.8s ease-in-out infinite;
        }
        @keyframes lp-scroll-dot {
          0%   { transform: translateY(0); opacity: 1; }
          70%  { transform: translateY(13px); opacity: 0; }
          100% { transform: translateY(0); opacity: 0; }
        }
        .lp-scroll-chevs {
          display: flex; flex-direction: column; align-items: center;
          line-height: 0.5;
          color: #a855f7;
          font-size: 0.9rem;
          margin-top: -2px;
        }
        .lp-scroll-chevs i { font-style: normal; animation: lp-chev 1.6s ease-in-out infinite; }
        .lp-scroll-chevs i:nth-child(2) { animation-delay: 0.25s; }
        @keyframes lp-chev {
          0%, 100% { opacity: 0.25; transform: translateY(0); }
          50%      { opacity: 1; transform: translateY(3px); }
        }

        /* ===== SECTIONS (shared) ===== */
        .lp-section { position: relative; padding: 5.5rem 1.5rem; max-width: 1150px; margin: 0 auto; }
        .lp-section-head { text-align: center; margin-bottom: 3.5rem; }
        .lp-section-label {
          font-family: 'Space Mono', monospace;
          font-size: 0.72rem; font-weight: 700;
          letter-spacing: 4px; text-transform: uppercase;
          color: #c084fc;
          margin-bottom: 1rem;
        }
        .lp-section-title {
          font-family: 'Unbounded', 'Outfit', sans-serif;
          font-size: clamp(1.7rem, 4.5vw, 2.9rem);
          font-weight: 800;
          color: #fff;
          margin: 0;
          line-height: 1.15;
        }
        .lp-section-sub {
          color: #a1a1aa;
          font-size: 1.02rem;
          margin: 1rem auto 0;
          max-width: 520px;
          line-height: 1.6;
        }
        /* Gradient divider between sections */
        .lp-divider {
          max-width: 1150px;
          height: 1px;
          margin: 0 auto;
          background: linear-gradient(90deg, transparent, rgba(168,85,247,0.3), transparent);
        }

        /* ===== STATS BAR ===== */
        .lp-stats {
          position: relative;
          border-top: 1px solid rgba(168,85,247,0.15);
          border-bottom: 1px solid rgba(168,85,247,0.15);
          background:
            radial-gradient(ellipse 60% 120% at 50% 0%, rgba(168,85,247,0.06), transparent 70%),
            rgba(255,255,255,0.012);
        }
        .lp-stats-grid {
          max-width: 1150px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1rem;
          padding: 3.5rem 1.5rem;
        }
        .lp-stat { text-align: center; padding: 0.5rem; }
        .lp-stat-value {
          font-family: 'Unbounded', 'Outfit', sans-serif;
          font-size: clamp(1.7rem, 3.8vw, 2.7rem);
          font-weight: 800;
          color: #fff;
          text-shadow: 0 0 32px rgba(168,85,247,0.5);
          line-height: 1.1;
        }
        .lp-stat-label {
          margin-top: 0.55rem;
          font-size: 0.78rem; font-weight: 600;
          letter-spacing: 2px; text-transform: uppercase;
          color: #71717a;
        }

        /* ===== HOW IT WORKS ===== */
        .lp-steps {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.4rem;
          position: relative;
        }
        .lp-step-card {
          position: relative;
          height: 100%;
          padding: 2.2rem 1.8rem;
          border-radius: 18px;
          background: rgba(255,255,255,0.022);
          border: 1px solid rgba(255,255,255,0.07);
          overflow: hidden;
          transition: transform 0.3s ease, border-color 0.3s ease;
        }
        .lp-step-card:hover { transform: translateY(-6px); border-color: rgba(168,85,247,0.4); }
        .lp-step-card::before {
          content: '';
          position: absolute; top: 0; left: 0; right: 0; height: 2px;
          background: linear-gradient(90deg, transparent, rgba(168,85,247,0.65), transparent);
          opacity: 0; transition: opacity 0.3s ease;
        }
        .lp-step-card:hover::before { opacity: 1; }
        .lp-step-num {
          position: absolute; top: 0.4rem; right: 1rem;
          font-family: 'Unbounded', sans-serif;
          font-size: 4.6rem; font-weight: 900;
          color: rgba(168,85,247,0.09);
          line-height: 1;
          pointer-events: none;
        }
        .lp-step-tag {
          font-family: 'Space Mono', monospace;
          font-size: 0.68rem; font-weight: 700;
          letter-spacing: 3px;
          color: #c084fc;
          margin-bottom: 1.1rem;
        }
        .lp-step-title {
          font-family: 'Unbounded', sans-serif;
          font-size: 1.12rem; font-weight: 700;
          color: #fff;
          margin: 0 0 0.8rem;
        }
        .lp-step-desc { color: #a1a1aa; font-size: 0.93rem; line-height: 1.65; margin: 0; }
        .lp-step-desc strong { color: #fbbf24; }

        /* ===== FEATURES ===== */
        .lp-features-bg {
          position: absolute; inset: 0;
          background: radial-gradient(ellipse 50% 45% at 80% 25%, rgba(168,85,247,0.07), transparent 70%);
          pointer-events: none;
        }
        .lp-features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(290px, 1fr));
          gap: 1.3rem;
        }
        .lp-feature-card {
          position: relative;
          height: 100%;
          padding: 1.9rem 1.7rem;
          border-radius: 18px;
          background: linear-gradient(160deg, rgba(255,255,255,0.035), rgba(255,255,255,0.012));
          border: 1px solid rgba(255,255,255,0.08);
          cursor: pointer;
          transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
        }
        .lp-feature-card:hover, .lp-feature-card:focus-visible {
          transform: translateY(-6px) scale(1.02);
          border-color: rgba(168,85,247,0.5);
          box-shadow: 0 18px 45px rgba(0,0,0,0.5), 0 0 34px rgba(168,85,247,0.16);
          outline: none;
        }
        .lp-feature-icon {
          width: 52px; height: 52px;
          display: flex; align-items: center; justify-content: center;
          font-size: 1.55rem;
          border-radius: 14px;
          background: linear-gradient(135deg, rgba(168,85,247,0.17), rgba(251,191,36,0.08));
          border: 1px solid rgba(168,85,247,0.28);
          margin-bottom: 1.2rem;
          transition: transform 0.3s ease;
        }
        .lp-feature-card:hover .lp-feature-icon { transform: scale(1.12) rotate(-6deg); }
        .lp-feature-title {
          display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap;
          font-family: 'Unbounded', sans-serif;
          font-size: 1.02rem; font-weight: 700;
          color: #fff;
          margin: 0 0 0.7rem;
        }
        .lp-feature-badge {
          font-family: 'Space Mono', monospace;
          font-size: 0.66rem; font-weight: 700;
          padding: 0.18rem 0.55rem;
          border-radius: 999px;
        }
        .lp-feature-badge.gold {
          color: #fbbf24;
          background: rgba(251,191,36,0.12);
          border: 1px solid rgba(251,191,36,0.3);
        }
        .lp-feature-badge.soon {
          color: #d8b4fe;
          background: rgba(168,85,247,0.12);
          border: 1px solid rgba(168,85,247,0.35);
        }
        .lp-feature-desc { color: #a1a1aa; font-size: 0.92rem; line-height: 1.6; margin: 0; }
        .lp-feature-go {
          display: inline-block;
          margin-top: 1rem;
          font-size: 0.82rem; font-weight: 700;
          color: #c084fc;
          opacity: 0;
          transform: translateX(-6px);
          transition: opacity 0.25s ease, transform 0.25s ease;
        }
        .lp-feature-card:hover .lp-feature-go { opacity: 1; transform: translateX(0); }

        /* Coming soon cards — identical dimensions to live cards, just not clickable */
        .lp-feature-card.lp-soon { cursor: default; }
        .lp-feature-card.lp-soon:hover {
          transform: translateY(-3px);
          border-color: rgba(168,85,247,0.35);
          box-shadow: 0 12px 30px rgba(0,0,0,0.4);
        }

        /* ===== FEATURED GIVEAWAY BANNER (real post embedded inside) ===== */
        .lp-ga-banner {
          display: flex;
          align-items: center;
          gap: 2.5rem;
          padding: 2.1rem 2.3rem;
          border-radius: 20px;
          background: linear-gradient(120deg, rgba(251,191,36,0.1), rgba(168,85,247,0.07) 60%, rgba(251,191,36,0.04));
          border: 1px solid rgba(251,191,36,0.45);
          animation: lp-ga-glow 2.6s ease-in-out infinite;
        }
        @keyframes lp-ga-glow {
          0%, 100% { box-shadow: 0 0 22px rgba(251,191,36,0.15), 0 14px 40px rgba(0,0,0,0.45); }
          50%      { box-shadow: 0 0 45px rgba(251,191,36,0.32), 0 14px 40px rgba(0,0,0,0.45); }
        }
        .lp-ga-content { flex: 1; min-width: 0; }
        .lp-ga-side { width: min(430px, 100%); flex-shrink: 0; }
        .lp-ga-badge {
          font-family: 'Space Mono', monospace;
          font-size: 0.68rem; font-weight: 700;
          letter-spacing: 3px; text-transform: uppercase;
          color: #fbbf24;
          margin-bottom: 0.8rem;
        }
        .lp-ga-title {
          font-family: 'Unbounded', 'Outfit', sans-serif;
          font-size: clamp(1.15rem, 2.6vw, 1.6rem);
          font-weight: 800;
          color: #fff;
          margin: 0 0 0.6rem;
          line-height: 1.25;
        }
        .lp-ga-desc { color: #a1a1aa; font-size: 0.95rem; line-height: 1.6; margin: 0 0 1.2rem; max-width: 560px; }
        .lp-ga-cta {
          display: inline-flex; align-items: center; gap: 0.5rem;
          padding: 0.75rem 1.7rem;
          border-radius: 12px;
          background: linear-gradient(135deg, #fbbf24, #f59e0b);
          color: #1c1205;
          font-size: 0.92rem; font-weight: 800;
          text-decoration: none;
          transition: transform 0.25s ease, box-shadow 0.25s ease;
        }
        .lp-ga-cta:hover { transform: translateY(-2px); box-shadow: 0 0 24px rgba(251,191,36,0.4); }
        .lp-ga-img {
          width: 230px; height: 150px;
          object-fit: cover;
          border-radius: 14px;
          border: 1px solid rgba(255,255,255,0.12);
          flex-shrink: 0;
        }

        /* ===== LEADERBOARD PREVIEW ===== */
        .lp-lb-card {
          max-width: 680px;
          margin: 0 auto;
          border-radius: 22px;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.09);
          backdrop-filter: blur(14px);
          box-shadow: 0 28px 70px rgba(0,0,0,0.55);
          overflow: hidden;
        }
        .lp-lb-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 1.2rem 1.5rem;
          border-bottom: 1px solid rgba(255,255,255,0.07);
          background: linear-gradient(90deg, rgba(168,85,247,0.1), transparent 60%);
        }
        .lp-lb-header-title {
          font-family: 'Unbounded', sans-serif;
          font-size: 0.92rem; font-weight: 700;
          color: #fff;
        }
        .lp-lb-pool {
          font-family: 'Space Mono', monospace;
          font-size: 0.7rem; font-weight: 700;
          color: #fbbf24;
          letter-spacing: 1px;
        }
        .lp-lb-row {
          display: flex; align-items: center; gap: 1rem;
          padding: 0.95rem 1.5rem;
          border-bottom: 1px solid rgba(255,255,255,0.045);
          transition: background 0.25s ease;
        }
        .lp-lb-row:hover { background: rgba(168,85,247,0.05); }
        .lp-lb-row:last-child { border-bottom: none; }
        .lp-lb-rank {
          font-family: 'Unbounded', sans-serif;
          font-size: 0.95rem; font-weight: 800;
          width: 34px; flex-shrink: 0;
        }
        .lp-lb-avatar {
          width: 36px; height: 36px;
          border-radius: 50%;
          flex-shrink: 0;
          object-fit: cover;
          border: 1px solid rgba(255,255,255,0.15);
        }
        .lp-lb-avatar-fallback {
          width: 36px; height: 36px;
          border-radius: 50%;
          flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          font-weight: 800; font-size: 0.9rem;
          color: #fff;
          background: linear-gradient(135deg, #7c3aed, #a855f7);
        }
        .lp-lb-name { font-weight: 600; font-size: 0.95rem; color: #fff; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .lp-lb-wager {
          font-family: 'Space Mono', monospace;
          font-size: 0.85rem; font-weight: 700;
          color: #e4e4e7;
        }
        .lp-lb-prize {
          font-family: 'Space Mono', monospace;
          font-size: 0.72rem; font-weight: 700;
          color: #22c55e;
          background: rgba(34,197,94,0.1);
          border: 1px solid rgba(34,197,94,0.25);
          padding: 0.2rem 0.55rem;
          border-radius: 999px;
          flex-shrink: 0;
        }
        /* Shimmer skeleton placeholders while standings load */
        .lp-skel {
          border-radius: 6px;
          background: linear-gradient(90deg, rgba(168,85,247,0.07) 25%, rgba(168,85,247,0.16) 50%, rgba(168,85,247,0.07) 75%);
          background-size: 200% 100%;
          animation: lp-shimmer 1.6s linear infinite;
        }
        @keyframes lp-shimmer {
          from { background-position: 200% 0; }
          to   { background-position: -200% 0; }
        }
        .lp-skel-avatar { width: 36px; height: 36px; border-radius: 50%; flex-shrink: 0; }
        .lp-skel-name { height: 12px; flex: 1; max-width: 180px; }
        .lp-skel-wager { height: 12px; width: 80px; margin-left: auto; }
        .lp-lb-footer { padding: 1.2rem 1.5rem; text-align: center; }
        .lp-lb-view-btn {
          display: inline-flex; align-items: center; gap: 0.5rem;
          padding: 0.85rem 1.9rem;
          border-radius: 12px;
          border: 1px solid rgba(168,85,247,0.45);
          background: rgba(168,85,247,0.1);
          color: #d8b4fe;
          font-family: 'Outfit', sans-serif;
          font-size: 0.95rem; font-weight: 700;
          cursor: pointer;
          transition: transform 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;
        }
        .lp-lb-view-btn:hover {
          transform: translateY(-2px);
          background: rgba(168,85,247,0.2);
          box-shadow: 0 0 26px rgba(168,85,247,0.25);
        }

        /* ===== FAQ ===== */
        .lp-faq-list { max-width: 720px; margin: 0 auto; display: flex; flex-direction: column; gap: 0.85rem; }
        .lp-faq-item {
          border-radius: 16px;
          background: rgba(255,255,255,0.025);
          border: 1px solid rgba(255,255,255,0.07);
          overflow: hidden;
          transition: border-color 0.3s ease;
        }
        .lp-faq-item.open { border-color: rgba(168,85,247,0.45); }
        .lp-faq-question {
          width: 100%;
          display: flex; justify-content: space-between; align-items: center; gap: 1rem;
          padding: 1.25rem 1.5rem;
          background: none; border: none; cursor: pointer;
          color: #fff;
          font-family: 'Outfit', sans-serif;
          font-size: 1.02rem; font-weight: 600;
          text-align: left;
        }
        .lp-faq-icon {
          flex-shrink: 0;
          width: 28px; height: 28px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 50%;
          border: 1px solid rgba(168,85,247,0.45);
          color: #c084fc;
          font-size: 1.1rem; font-weight: 400;
          transition: transform 0.35s ease, background 0.35s ease;
        }
        .lp-faq-item.open .lp-faq-icon { transform: rotate(45deg); background: rgba(168,85,247,0.14); }
        .lp-faq-body {
          display: grid;
          grid-template-rows: 0fr;
          transition: grid-template-rows 0.45s ease-out;
        }
        .lp-faq-body.open { grid-template-rows: 1fr; }
        .lp-faq-body > div { overflow: hidden; }
        .lp-faq-answer {
          padding: 0 1.5rem 1.35rem;
          margin: 0;
          color: #a1a1aa;
          font-size: 0.93rem;
          line-height: 1.7;
        }

        /* ===== FINAL CTA + FOOTER ===== */
        .lp-final-cta {
          text-align: center;
          padding: 6.5rem 1.5rem;
          position: relative;
          overflow: hidden;
        }
        .lp-final-cta-glow {
          position: absolute;
          width: 700px; height: 380px;
          left: 50%; top: 50%;
          transform: translate(-50%, -50%);
          background: radial-gradient(ellipse, rgba(168,85,247,0.13), rgba(251,191,36,0.06) 55%, transparent 75%);
          filter: blur(50px);
          pointer-events: none;
        }
        .lp-footer {
          border-top: 1px solid rgba(255,255,255,0.06);
          padding: 3rem 1.5rem 2.2rem;
          text-align: center;
        }
        .lp-footer-links { display: flex; justify-content: center; gap: 1rem; flex-wrap: wrap; margin-bottom: 1.8rem; }
        .lp-footer-link {
          display: inline-flex; align-items: center; gap: 0.55rem;
          padding: 0.7rem 1.4rem;
          border-radius: 12px;
          background: rgba(255,255,255,0.035);
          border: 1px solid rgba(255,255,255,0.09);
          color: #e4e4e7;
          font-size: 0.9rem; font-weight: 600;
          text-decoration: none;
          transition: transform 0.25s ease, border-color 0.25s ease, color 0.25s ease;
        }
        .lp-footer-link svg { width: 17px; height: 17px; }
        .lp-footer-link:hover { transform: translateY(-2px); }
        .lp-footer-link.discord:hover { border-color: rgba(88,101,242,0.6); color: #818cf8; }
        .lp-footer-link.twitch:hover { border-color: rgba(145,70,255,0.6); color: #a855f7; }
        .lp-footer-link.x:hover { border-color: rgba(255,255,255,0.4); color: #fff; }
        .lp-footer-link.instagram:hover { border-color: rgba(225,48,108,0.6); color: #f472b6; }
        .lp-footer-link.youtube:hover { border-color: rgba(255,0,0,0.55); color: #f87171; }
        .lp-footer-link.shuffle:hover { border-color: rgba(251,191,36,0.6); color: #fbbf24; }
        .lp-footer-legal {
          max-width: 620px; margin: 0 auto 1.6rem;
          color: #a1a1aa; font-size: 0.8rem; line-height: 1.75;
        }
        .lp-footer-legal-links {
          display: flex; justify-content: center; gap: 0.5rem 1.4rem; flex-wrap: wrap;
          margin-bottom: 1.1rem;
        }
        .lp-footer-legal-links a {
          color: #a1a1aa; font-size: 0.82rem; font-weight: 600;
          text-decoration: underline; text-underline-offset: 3px;
        }
        .lp-footer-legal-links a:hover { color: #e9d5ff; }
        .lp-footer-legal p { margin: 0.6rem 0 0; }
        .lp-footer-legal a { color: #c4b5fd; text-decoration: underline; text-underline-offset: 3px; }
        .lp-footer-legal a:hover { color: #e9d5ff; }
        .lp-footer-18 {
          display: inline-flex; align-items: center; justify-content: center;
          padding: 0.2rem 0.65rem; border-radius: 6px;
          border: 1.5px solid rgba(251,191,36,0.55); color: #fbbf24;
          font-size: 0.82rem; font-weight: 800; letter-spacing: 0.05em;
        }
        .lp-footer-note { color: #8b8b96; font-size: 0.78rem; line-height: 1.8; max-width: 560px; margin: 0 auto; }
        .lp-footer-note strong { color: #a1a1aa; }
        .lp-footer-dot { margin: 0 0.45rem; color: #71717a; }
        .lp-footer-credit { color: #8b8b96; text-decoration: underline; text-underline-offset: 3px; }
        .lp-footer-credit:hover { color: #c4b5fd; }

        /* ===== RESPONSIVE ===== */
        @media (max-width: 900px) {
          .lp-steps { grid-template-columns: 1fr; }
          .lp-stats-grid { grid-template-columns: repeat(2, 1fr); gap: 2rem 1rem; }
          .lp-section { padding: 4.5rem 1.25rem; }
          .lp-hero { min-height: calc(100svh - 60px); }
          .lp-ga-banner { flex-direction: column; align-items: stretch; gap: 1.4rem; }
          .lp-ga-side { width: 100%; }
          .lp-ga-img { width: 100%; height: 170px; }
        }
        @media (max-width: 520px) {
          .lp-hero-ctas { flex-direction: column; width: 100%; max-width: 320px; margin: 0 auto; }
          .lp-btn-primary, .lp-btn-ghost { justify-content: center; }
          .lp-btn-mega { padding: 1.2rem 2rem; width: 100%; max-width: 340px; }
          .lp-lb-wager { display: none; }
          .lp-scroll-hint { display: none; }
          .lp-hero-rewards { margin-top: 2.25rem; }
        }

      `}</style>

      {/* ============ 1. HERO ============ */}
      <section className="lp-hero lp-snap-point">
        <div className="lp-aurora-layer">
          <div className="lp-aurora lp-aurora-a" />
          <div className="lp-aurora lp-aurora-b" />
          <div className="lp-aurora lp-aurora-c" />
        </div>
        {particles.map((p, i) => (
          <span
            key={i}
            className="lp-particle"
            style={{
              left: `${p.left}%`,
              width: `${p.size}px`,
              height: `${p.size}px`,
              background: p.color,
              boxShadow: `0 0 ${p.size * 2}px ${p.color}`,
              filter: p.blur ? `blur(${p.blur}px)` : undefined,
              '--lp-po': p.opacity,
              '--lp-px': `${p.drift}px`,
              animationDuration: `${p.duration}s`,
              animationDelay: `${p.delay}s`
            }}
          />
        ))}

        <div style={{ position: 'relative', zIndex: 2, width: '100%' }}>
          <div className="lp-hero-enter" style={{ animationDelay: '0.1s' }}>
            <a
              href={TWITCH_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={`lp-live-badge ${isStreamerLive ? 'live' : 'offline'}`}
            >
              <span className="lp-live-dot" />
              {isStreamerLive ? 'Live on Twitch — Watch Now' : 'Offline — Follow on Twitch'}
            </a>
          </div>

          <h1 className="lp-hero-title lp-display lp-hero-enter" style={{ animationDelay: '0.25s' }}>
            SNUTZY
          </h1>
          <div className="lp-hero-x lp-display lp-hero-enter" style={{ animationDelay: '0.4s' }}>
            <span className="lp-x-line" />
            <span className="lp-x-mark">×</span>
            <span>Shuffle</span>
            <span className="lp-x-line r" />
          </div>

          <p className="lp-hero-sub lp-hero-enter" style={{ animationDelay: '0.55s' }}>
            The most rewarding community on Shuffle. Wager under guild <strong>SNUTZY</strong> to
            climb leaderboards, win giveaways and unlock exclusive rewards — just for playing.
          </p>

          <div className="lp-hero-ctas lp-hero-enter" style={{ animationDelay: '0.7s' }}>
            <a href={SHUFFLE_URL} target="_blank" rel="noopener noreferrer" className="lp-btn-primary">
              Sign Up with Shuffle <span className="lp-btn-arrow">→</span>
            </a>
            <button className="lp-btn-ghost" onClick={scrollToRewards}>
              Explore Rewards <span className="lp-bob">↓</span>
            </button>
          </div>

          <div className="lp-hero-rewards lp-hero-enter" style={{ animationDelay: '0.85s' }}>
            <div className="lp-hr-eyebrow" aria-hidden="true">[ What you can win ]</div>
            <div className={`lp-hr-grid cols-${heroCols}`} role="group" aria-label="What you can win">
              {heroRewards.map((r) => (
                <button
                  key={r.label}
                  className="lp-hr-cell"
                  onClick={() => (r.page ? goTo(r.page, r.sub) : scrollToRewards())}
                >
                  <span className="lp-hr-go" aria-hidden="true">→</span>
                  <span className={`lp-hr-value lp-display${r.money ? ' money' : ''}${r.text ? ' text' : ''}`}>{r.value}</span>
                  <span className="lp-hr-label">{r.label}</span>
                  <span className="lp-hr-fact">{r.fact}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <button className="lp-scroll-hint" onClick={scrollToStats}>
          <div className="lp-scroll-mouse"><span /></div>
          <div className="lp-scroll-chevs"><i>⌄</i><i>⌄</i></div>
          Scroll to Explore
        </button>
      </section>

      {/* ============ 2. STATS BAR ============ */}
      <section className="lp-stats lp-snap-point" id="lp-stats">
        <div className="lp-stats-grid">
          <Reveal delay={0}>
            <div className="lp-stat">
              <div className="lp-stat-value"><CountUp value={1.5} suffix="M+" decimals={1} /></div>
              <div className="lp-stat-label">Hours Watched</div>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="lp-stat">
              <div className="lp-stat-value"><CountUp value={activePlayers || 1300} suffix="+" /></div>
              <div className="lp-stat-label">Active Players</div>
            </div>
          </Reveal>
          <Reveal delay={240}>
            <div className="lp-stat">
              <div className="lp-stat-value"><CountUp value={totalGivenAway} prefix="$" /></div>
              <div className="lp-stat-label">Given Away</div>
            </div>
          </Reveal>
          <Reveal delay={360}>
            <div className="lp-stat">
              <div className="lp-stat-value"><CountUp value={1500} suffix="+" /></div>
              <div className="lp-stat-label">Giveaways Run</div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ FEATURED GIVEAWAY (admin-curated, shown when one is live) ============ */}
      {giveawayPost && (
        <section className="lp-section" style={{ paddingTop: '3.5rem', paddingBottom: '0' }}>
          <Reveal>
            <div className="lp-ga-banner">
              <div className="lp-ga-content">
                <div className="lp-ga-badge">[ FEATURED GIVEAWAY ]</div>
                <h3 className="lp-ga-title">{giveawayPost.title}</h3>
                {giveawayPost.description && <p className="lp-ga-desc">{giveawayPost.description}</p>}
                <a href={giveawayPost.url} target="_blank" rel="noopener noreferrer" className="lp-ga-cta">
                  🎁 Enter on {(PLATFORMS[giveawayPost.platform] || {}).label || 'social'} →
                </a>
              </div>
              {/* The real post embedded inside the banner */}
              {['twitter', 'instagram', 'youtube'].includes(giveawayPost.platform) ? (
                <div className="lp-ga-side"><SocialEmbed post={giveawayPost} /></div>
              ) : giveawayPost.image_url ? (
                <img src={giveawayPost.image_url} alt="" className="lp-ga-img" />
              ) : null}
            </div>
          </Reveal>
        </section>
      )}

      {/* ============ 3. HOW IT WORKS ============ */}
      <section className="lp-section lp-snap-point">
        <SectionHead
          label="How It Works"
          title="Your Pathway to the Payouts"
          sub="Three steps. A few minutes. Then every wager and every stream starts working for you."
        />
        <div className="lp-steps">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 150}>
              <div className="lp-step-card">
                <div className="lp-step-num lp-display">{s.n}</div>
                <div className="lp-step-tag">[ STEP {i + 1} ]</div>
                <h3 className="lp-step-title">{s.title}</h3>
                <p className="lp-step-desc">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <div className="lp-divider" />

      {/* ============ 4. FEATURES ============ */}
      <section className="lp-section lp-snap-point" id="lp-rewards">
        <div className="lp-features-bg" />
        <SectionHead
          label="The Platform"
          title="Everything You Can Win"
          sub="Leaderboards, giveaways and more — all stacked on top of your normal play. Click any card to dive in."
        />
        <div className="lp-features-grid">
          {liveFeatures.map((f, i) => (
            <Reveal key={f.title} delay={(i % 3) * 130}>
              <div
                className="lp-feature-card"
                role={f.page ? 'button' : undefined}
                tabIndex={f.page ? 0 : undefined}
                onClick={() => f.page && goTo(f.page)}
                onKeyDown={(e) => f.page && cardKeyDown(e, f.page)}
                style={f.page ? undefined : { cursor: 'default' }}
              >
                <div className="lp-feature-icon">{f.icon}</div>
                <h3 className="lp-feature-title">
                  {f.title}
                  {f.badge && <span className={`lp-feature-badge ${f.badgeType}`}>{f.badge}</span>}
                </h3>
                <p className="lp-feature-desc">{f.desc}</p>
                {f.page && <span className="lp-feature-go">Check it out →</span>}
              </div>
            </Reveal>
          ))}
        </div>

        {/* Coming soon — same card dimensions as the grid above, badge marks the difference */}
        <div className="lp-features-grid" style={{ marginTop: '1.3rem' }}>
          {comingSoon.map((f, i) => (
            <Reveal key={f.title} delay={i * 130}>
              <div className="lp-feature-card lp-soon">
                <div className="lp-feature-icon">{f.icon}</div>
                <h3 className="lp-feature-title">
                  {f.title}
                  <span className="lp-feature-badge soon">COMING SOON</span>
                </h3>
                <p className="lp-feature-desc">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <div className="lp-divider" />

      {/* ============ 5. LEADERBOARD PREVIEW ============ */}
      <section className="lp-section lp-snap-point">
        <SectionHead
          label="Live Standings"
          title="The Monthly Race"
          sub={`The top 15 split ${POOL} every month. Here's who's leading right now.`}
        />
        <Reveal delay={150}>
          <div className="lp-lb-card">
            <div className="lp-lb-header">
              <span className="lp-lb-header-title">🏆 Top 5 — This Month</span>
              <span className="lp-lb-pool">{POOL} POOL</span>
            </div>
            {topFive.length === 0 ? (
              // Shimmer placeholders so the section looks alive before/without API data
              [1, 2, 3, 4, 5].map((r) => (
                <div className="lp-lb-row" key={r}>
                  <span className="lp-lb-rank" style={{ color: rankColors[r] || '#71717a' }}>#{r}</span>
                  <div className="lp-skel lp-skel-avatar" />
                  <div className="lp-skel lp-skel-name" style={{ maxWidth: `${190 - r * 18}px` }} />
                  <div className="lp-skel lp-skel-wager" />
                </div>
              ))
            ) : (
              topFive.map((entry) => (
                <div className="lp-lb-row" key={entry.rank}>
                  <span className="lp-lb-rank" style={{ color: rankColors[entry.rank] || '#71717a' }}>
                    #{entry.rank}
                  </span>
                  {entry.profile_image_url ? (
                    <img src={entry.profile_image_url} alt="" className="lp-lb-avatar" />
                  ) : (
                    <div className="lp-lb-avatar-fallback">{(entry.username || '?').charAt(0).toUpperCase()}</div>
                  )}
                  <span className="lp-lb-name">{entry.username}</span>
                  <span className="lp-lb-wager">${Math.round(entry.wagers || 0).toLocaleString()}</span>
                  {prizeFor(entry.rank) && <span className="lp-lb-prize">{prizeFor(entry.rank)}</span>}
                </div>
              ))
            )}
            <div className="lp-lb-footer">
              <button className="lp-lb-view-btn" onClick={() => goTo('leaderboard')}>
                View Full Leaderboard →
              </button>
            </div>
          </div>
        </Reveal>
      </section>

      <div className="lp-divider" />

      {/* ============ 6. FAQ ============ */}
      <section className="lp-section lp-snap-point">
        <SectionHead label="FAQ" title="Questions, Answered" />
        <div className="lp-faq-list">
          {FAQS.map((f, i) => (
            <Reveal key={f.q} delay={i * 100}>
              <FaqItem
                q={f.q}
                a={f.a}
                open={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? null : i)}
              />
            </Reveal>
          ))}
        </div>
      </section>

      <div className="lp-divider" />

      {/* ============ FINAL CTA ============ */}
      <section className="lp-final-cta lp-snap-point">
        <div className="lp-final-cta-glow" />
        <Reveal>
          <div className="lp-section-label">[ JOIN THE GUILD ]</div>
        </Reveal>
        <Reveal delay={120}>
          <h2 className="lp-section-title lp-display" style={{ marginBottom: '1.2rem' }}>
            Ready to Run It Up?
          </h2>
        </Reveal>
        <Reveal delay={240}>
          <p className="lp-section-sub" style={{ marginBottom: '2.6rem' }}>
            Join thousands of players earning rewards in guild <strong style={{ color: '#fbbf24' }}>SNUTZY</strong>.
          </p>
        </Reveal>
        <Reveal delay={360}>
          <a href={SHUFFLE_URL} target="_blank" rel="noopener noreferrer" className="lp-btn-primary lp-btn-mega">
            Sign Up to Guild SNUTZY <span className="lp-btn-arrow">→</span>
          </a>
        </Reveal>
      </section>

      {/* ============ 7. FOOTER ============ */}
      <footer className="lp-footer">
        <div className="lp-footer-links">
          <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" className="lp-footer-link discord">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>
            Discord
          </a>
          <a href={TWITCH_URL} target="_blank" rel="noopener noreferrer" className="lp-footer-link twitch">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/></svg>
            Twitch
          </a>
          <a href={X_URL} target="_blank" rel="noopener noreferrer" className="lp-footer-link x">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            X
          </a>
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="lp-footer-link instagram">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
            Instagram
          </a>
          <a href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer" className="lp-footer-link youtube">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
            YouTube
          </a>
          <a href={SHUFFLE_URL} target="_blank" rel="noopener noreferrer" className="lp-footer-link shuffle">
            🎰 Join Shuffle
          </a>
        </div>
        <div className="lp-footer-legal">
          <nav aria-label="Legal" className="lp-footer-legal-links">
            <a href="/legal#terms">Terms of Use</a>
            <a href="/legal#rewards">Rewards Terms</a>
            <a href="/legal#privacy">Privacy</a>
            <a href="/legal#responsible-gambling">Responsible Gambling</a>
          </nav>
          <span className="lp-footer-18" aria-label="18 plus only">18+</span>
          <p>
            Gambling involves risk — only play with money you can afford to lose.
            If it stops being fun, get free confidential support at{' '}
            <a href="https://www.begambleaware.org" target="_blank" rel="noopener noreferrer">BeGambleAware.org</a> or{' '}
            <a href="https://www.gamblingtherapy.org" target="_blank" rel="noopener noreferrer">GamblingTherapy.org</a>.
          </p>
          <p>
            SNUTZY is a Shuffle affiliate and earns commission when you play under guild SNUTZY.
            Shuffle is unavailable in some territories — check{' '}
            <a href="https://shuffle.com/info/terms" target="_blank" rel="noopener noreferrer">Shuffle's terms</a> for eligibility.
            Questions about rewards or payouts? Open a ticket in our{' '}
            <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer">Discord</a>.
          </p>
        </div>
        <p className="lp-footer-note">
          <strong>© {new Date().getFullYear()} SNUTZY</strong>
          <span className="lp-footer-dot">·</span>
          made by <a href="http://discord.com/users/182121536461864960" target="_blank" rel="noopener noreferrer" className="lp-footer-credit">@pa1n</a>
        </p>
      </footer>
    </div>
  );
}
